/**
 * «Попросить» — money requests between members of one group (docs/cabinet-redesign/07 §4.3, §4.6, owner answers §0.1).
 * Customer-JWT zone — /api/v1/showcase/me/money-requests, answered by ShowcaseMoneyRequestController
 * (MoneyRequestService, MoneyRequestViews). The requester asks a member whose shared balance is on; the payer approves
 * (a step-up of purpose MEMBER_TOP_UP, a smaller amount allowed) or declines from the request's card in the chat.
 * A refusal is `{"error": CODE}` (translated under vitrine.error.*), as the master account's.
 */
import { http } from './http';
import type { Decimal, MemberCircle, ToCardResult } from './masterAccount';

const BASE = '/v1/showcase/me/money-requests';

/** Who looks at the request (MoneyRequestViews.Side) — the client is the payer or the requester. */
export type MoneyRequestSide = 'PAYER' | 'REQUESTER' | 'MANAGER';

/**
 * Where the request is, the same for both sides (MoneyRequestViews.State): PENDING — waits for the payer; APPROVED — the
 * money is on its way; CREDITED — on the card; DECLINED; EXPIRED; CANCELLED — withdrawn or replaced; FAILED — approved
 * but it did not reach the card, the amount stayed with the payer.
 */
export type MoneyRequestState = 'PENDING' | 'APPROVED' | 'CREDITED' | 'DECLINED' | 'EXPIRED' | 'CANCELLED' | 'FAILED';

/** One side of the request: the client's id and their name as the other member sees it. */
export interface MoneyRequestParty {
  customerId: string;
  name: string | null;
}

/** The requester's card the money goes to (MoneyRequestViews.CardView). */
export interface MoneyRequestCard {
  cardId: string;
  displayName: string | null;
  lastFour: string | null;
  status: string | null;
  /** Issued within the last 24 hours — the payer's card warns about it (§4.9 Н11). */
  issuedRecently: boolean;
}

/** MoneyRequestViews.MoneyRequestView — the request as `side` sees it. Amounts in the coin's major units. */
export interface MoneyRequestView {
  id: string;
  side: MoneyRequestSide;
  state: MoneyRequestState;
  requester: MoneyRequestParty;
  payer: MoneyRequestParty;
  card: MoneyRequestCard;
  amount: Decimal;
  asset: string;
  note: string | null;
  approvedAmount: Decimal | null;
  topupRequestId: string | null;
  /** A code, never free text: WITHDRAWN, REPLACED, MUTED (payer only), or why the top-up failed. */
  closeReason: string | null;
  createdAt: string;
  expiresAt: string;
  decidedAt: string | null;
  /** The viewer's chat thread of this request. */
  ticketId: string | null;
  canApprove: boolean;
  canDecline: boolean;
  canCancel: boolean;
}

/** GET /payers (GroupMemberCards.PayerView): a member I may ask — their shared balance is on. */
export interface MoneyRequestPayer {
  payerId: string;
  name: string | null;
  circle: MemberCircle | null;
  roleLabel: string | null;
  role: string;
}

/** POST / body. `cardId` absent — my top-up card; `note` up to {@link MONEY_REQUEST_NOTE_MAX} characters. */
export interface CreateMoneyRequest {
  payerId: string;
  /** Plain decimal string in the coin's major units. */
  amount: string;
  asset: string;
  cardId?: string | null;
  note?: string | null;
}

/** POST /{id}/approve answer (MoneyRequestService.ApproveResult): the request and the card top-up it became. */
export interface ApproveResult {
  request: MoneyRequestView;
  topUp: ToCardResult;
}

/** The note's limit the server enforces (MONEY_REQUEST_NOTE_TOO_LONG). */
export const MONEY_REQUEST_NOTE_MAX = 140;

/** The coins a request may name (MoneyRequestService.ASSETS). */
export const MONEY_REQUEST_ASSETS = ['USDT', 'USDC'] as const;

export const moneyRequestsApi = {
  async payers(): Promise<MoneyRequestPayer[]> {
    return (await http.get<MoneyRequestPayer[]>(`${BASE}/payers`)).data;
  },

  /** Requests I asked for or was asked, newest first, each as my side sees it. */
  async mine(): Promise<MoneyRequestView[]> {
    return (await http.get<MoneyRequestView[]>(BASE)).data;
  },

  async one(id: string): Promise<MoneyRequestView> {
    return (await http.get<MoneyRequestView>(`${BASE}/${encodeURIComponent(id)}`)).data;
  },

  async create(cmd: CreateMoneyRequest): Promise<MoneyRequestView> {
    return (await http.post<MoneyRequestView>(BASE, {
      payerId: cmd.payerId, amount: cmd.amount, asset: cmd.asset, cardId: cmd.cardId ?? null, note: cmd.note ?? null,
    })).data;
  },

  /**
   * «Разрешить»: `amount` — the requested one or less (absent — as requested); `network` — my master address that pays.
   * Without `grant` the server answers 403 STEP_UP_REQUIRED with the factors once every other check passed; call
   * again with the grant of purpose MEMBER_TOP_UP. A repeat returns the same top-up.
   */
  async approve(id: string, body: { amount?: string | null; network: string }, grant?: string): Promise<ApproveResult> {
    const headers: Record<string, string> = grant ? { 'X-Reveal-Grant': grant } : {};
    return (await http.post<ApproveResult>(`${BASE}/${encodeURIComponent(id)}/approve`,
      { amount: body.amount ?? null, network: body.network }, { headers })).data;
  },

  async decline(id: string): Promise<MoneyRequestView> {
    return (await http.post<MoneyRequestView>(`${BASE}/${encodeURIComponent(id)}/decline`)).data;
  },

  /** The requester withdraws their request. */
  async cancel(id: string): Promise<MoneyRequestView> {
    return (await http.post<MoneyRequestView>(`${BASE}/${encodeURIComponent(id)}/cancel`)).data;
  },

  /** «Не получать запросы от …»: also drops their live request. Idempotent. */
  async mute(requesterId: string): Promise<void> {
    await http.post(`${BASE}/mute`, { requesterId });
  },

  async unmute(requesterId: string): Promise<void> {
    await http.delete(`${BASE}/mute/${encodeURIComponent(requesterId)}`);
  },

  /** The members I muted. */
  async mutes(): Promise<string[]> {
    return (await http.get<string[]>(`${BASE}/mutes`)).data;
  },
};
